import { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { ApiGet } from '../Api';
import Modal from 'react-modal';
import logo from './yeonoo_logo.png';
import { GetTime, GetDate } from '../Function';

function YeonooMonitoring2({ rotation = false }) {
  Modal.setAppElement('#root')
  
  const apiUrl = process.env.REACT_APP_API_DOMAIN + 'yeonoo/';
  const logoHeight = '60px'; //logo height + logo paddingTop
  const gridGap = window.innerWidth > 800 ? 30 : 20;
  const backgroundMainColor = 'rgb(12, 35, 64)';
  const backgroundSubColor = 'rgb(18, 46, 78)';
  const titleColor= 'rgb(153, 255, 153)';

  const [isMobile, setIsMobile] = useState(false);
  const [refreshTime, setRefreshTime] = useState('0001-01-01 00:00:00');
  const [gridStyle, setGridStyle] = useState(GetGridStyle());
  const [infraLineOption, setInfraLineOption] = useState(GetLineOption());
  const [serverLineOption, setServerLineOption] = useState(GetLineOption());
  const [airConditionerLineOption, setAirConditionerLineOption] = useState(GetLineOption());
  const [co2GaugeOption, setCo2GaugeOption] = useState(GetGaugeOption());
  const [totalGaugeOption, setTotalGaugeOption] = useState(GetGaugeOption());
  const [infraGaugeOption, setInfraGaugeOption] = useState(GetGaugeOption());
  const [serverGaugeOption, setServerGaugeOption] = useState(GetGaugeOption());
  const [airConditionerGaugeOption, setAirConditionerGaugeOption] = useState(GetGaugeOption());
  const [date, setDate] = useState({ infra: GetDate(), server: GetDate(), airConditioner: GetDate() });

  const chartRefs = useRef([]);
  let intervalId;

  function Resize() {
    setGridStyle(GetGridStyle());
  };

  useEffect(() => {
    document.title = 'YEONOO MONITORING';

    setIsMobile(/mobile/i.test(navigator.userAgent));
    window.addEventListener('resize', Resize);

    Inquiry();

    if (rotation) {
      intervalId = setInterval(()=>{
        Inquiry();
      }, 1000 * 60);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }

      window.removeEventListener('resize', Resize);

      for (const chartRef of chartRefs.current) {
        if (chartRef) {
          chartRef.dispose();
        }
      }
      chartRefs.current = [];

      setIsMobile(null);
      setRefreshTime(null);
      setGridStyle(null);
      setInfraLineOption(null);
      setServerLineOption(null);
      setAirConditionerLineOption(null);
      setCo2GaugeOption(null);
      setTotalGaugeOption(null);
      setInfraGaugeOption(null);
      setServerGaugeOption(null);
      setAirConditionerGaugeOption(null);
      setDate(null);
    };
  }, []);
  
  function ChangeFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }

    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) { //Firefox
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) { //Chrome, Safari, Opera
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { //IE/Edge
      elem.msRequestFullscreen();
    }
  }
  
  function Inquiry() {
    DrawLineChart();
    DrawGaugeChart();
    
    setRefreshTime(GetTime());
  }

  async function DrawGaugeChart() {
    const response = await ApiGet(apiUrl + 'monitoring/fems');
    if (response !== null && response.ok) {
      const json = await response.json();
      const jsonData = json.data;

      const co2Data = {};
      const infraData = {};
      const serverData = {};
      const airConditionerData = {};
      for (const index in jsonData) {
        const data = jsonData[index];
        data.value = Number(data.value);
        if (data.name === 'CO2') {
          co2Data.lastMonth !== undefined ? co2Data.currentMonth = data.value : co2Data.lastMonth = data.value;
        } else if (data.name === 'electricity') {
          if (data.location === 'Infra room') {
            infraData.lastMonth !== undefined ? infraData.currentMonth = data.value : infraData.lastMonth = data.value;
          } else if (data.location === 'Server') {
            serverData.lastMonth !== undefined ? serverData.currentMonth = data.value : serverData.lastMonth = data.value;
          } else if (data.location === 'A/C') {
            airConditionerData.lastMonth !== undefined ? airConditionerData.currentMonth = data.value : airConditionerData.lastMonth = data.value;
          } 
        }
      }

      const totalElectricity = { 
        lastMonth: (infraData.lastMonth + serverData.lastMonth + airConditionerData.lastMonth).toFixed(1), 
        currentMonth: (infraData.currentMonth + serverData.currentMonth + airConditionerData.currentMonth).toFixed(1)
      };

      setCo2GaugeOption(GetGaugeOption('CO2 발생량', co2Data.lastMonth, co2Data.currentMonth, 'kg'));
      setTotalGaugeOption(GetGaugeOption('전체 사용 전력량', totalElectricity.lastMonth, totalElectricity.currentMonth, 'kWh'));
      setInfraGaugeOption(GetGaugeOption('인프라팀 사용 전력량', infraData.lastMonth, infraData.currentMonth, 'kWh'));
      setServerGaugeOption(GetGaugeOption('서버 사용 전력량', serverData.lastMonth, serverData.currentMonth, 'kWh'));
      setAirConditionerGaugeOption(GetGaugeOption('A/C 사용 전력량', airConditionerData.lastMonth, airConditionerData.currentMonth, 'kWh'));
    }
  }

  async function DrawLineChart() {
    const response = await ApiGet(apiUrl + `monitoring/fems/current?infraDate=${date.infra}&serverDate=${date.server}&airConditionerDate=${date.airConditioner}`);
    if (response !== null && response.ok) {
      const json = await response.json();
      const jsonData = json.data;

      const infraRoom = [];
      const server = [];
      const airConditioner = [];
      for (const index in jsonData) {
        const data = jsonData[index];
        if (data.location === 'Infra room') {
          infraRoom.push({ name: data.measurement_time, value: [data.measurement_time, data.value]})
        } else if (data.location === 'Server') {
          server.push({ name: data.measurement_time, value: [data.measurement_time, data.value]})
        } else if (data.location === 'A/C') {
          airConditioner.push({ name: data.measurement_time, value: [data.measurement_time, data.value]})
        }
      }

      setInfraLineOption(GetLineOption('인프라팀 전류', infraRoom));
      setServerLineOption(GetLineOption('서버 전류', server));
      setAirConditionerLineOption(GetLineOption('A/C 전류', airConditioner));
    }
  }

  function GetGridStyle() {
    return {
      display: 'grid',
      gap: gridGap,
      padding: gridGap,
      backgroundColor: backgroundMainColor,
      width: '100vw',
      height: `calc(100vh - ${logoHeight})`,
      boxSizing: 'border-box', // 패딩을 포함하여 크기 계산
      overflow: 'auto',
      scrollbarWidth: 'none',
      gridTemplateColumns: window.innerWidth > 800 ? 'repeat(4, 1fr)' : 'repeat(1, 1fr)'
    };
  };

  const gridItemStyle = {
    backgroundColor: backgroundSubColor,
    borderRadius: '10px',
    height: `calc((100vh - ${gridGap*3}px - ${logoHeight}) / 2)`, // 행 수에 맞게 높이를 계산, 2행이니 세로 간격의 총합 = padding 20px * 2 + gap 20px = 60px,
  };

  function Reload() {
    setInfraLineOption(GetLineOption('인프라팀 전류', []));
    setServerLineOption(GetLineOption('서버 전류', []));
    setAirConditionerLineOption(GetLineOption('A/C 전류', []));
    setCo2GaugeOption(GetGaugeOption('CO2 발생량', 0, 0, 'kg'));
    setTotalGaugeOption(GetGaugeOption('전체 사용 전력량', 0, 0, 'kWh'));
    setInfraGaugeOption(GetGaugeOption('인프라팀 사용 전력량', 0, 0, 'kWh'));
    setServerGaugeOption(GetGaugeOption('서버 사용 전력량', 0, 0, 'kWh'));
    setAirConditionerGaugeOption(GetGaugeOption('A/C 사용 전력량', 0, 0, 'kWh'));

    Inquiry();
  }

  const buttonStyle = { backgroundColor: backgroundMainColor, color: 'rgb(204,204,204)', border: 'none', fontSize: window.innerWidth > 800 ? 30 : 40 };
  const eChartsStyle = { width: '100%', height: '100%' };

  function GetLineOption(title, data) {
    return {
      grid: {
        top: '20%',
        left: '10%',
        right: '10%',
        bottom: '20%'
      },
      title: {
        text: title,
        left: 'center',
        top: 0,
        textStyle: {
          color: titleColor,
          fontSize: window.innerWidth > 800 ? 30 : 25
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          animation: false
        }
      },
      xAxis: {
        type: 'time',
        //type: 'category',
        axisLine: {
          lineStyle: {
            color: 'white',
          }
        },
        axisLabel: {
          //interval: 2,  //모든 데이터에 레이블을 표시
          rotate: 45,    //레이블 각도
          //fontSize: 10,   //폰트 크기
        },
      },
      yAxis: {
        type: 'value',
        name : 'A',
        axisLine: {
          lineStyle: {
            color: 'white',
          }
        },
        splitLine: {
          show: false,
          lineStyle: {
            color: 'gray',
          }
        }
      },
      series: [
        {
          data: data,
          type: 'line',
          //smooth: true,
          /*label: {
            show: true,
            position: 'right', 
            color: 'white',
            fontSize: 16,
            textBorderColor: 'black',
            textBorderWidth: 2,
          },*/
          itemStyle: {
            color: 'yellow',
          },
        }
      ],
    };
  }

  function GetGaugeOption(title, data1, data2, unit) {
    return {
      title: {
        text: title,
        left: 'center',
        top: 20,
        textStyle: {
          color: titleColor,
          fontSize: 30
        }
      },
      series: [
        {
          type: 'gauge',
          radius: '65%',
          max: unit === 'kg' ? 400 : 4000,//Math.ceil((data1 > data2 ? data1 * 1.5 : data2 * 1.5) / 100) * 100,
          anchor: {
            show: true,
            showAbove: true,
            size: 18,
            itemStyle: {
              //color: 'yellow'
            },
          },
          pointer: {
            icon: 'path://M2.9,0.7L2.9,0.7c1.4,0,2.6,1.2,2.6,2.6v115c0,1.4-1.2,2.6-2.6,2.6l0,0c-1.4,0-2.6-1.2-2.6-2.6V3.3C0.3,1.9,1.4,0.7,2.9,0.7z',
            width: 5,
            length: '60%',
            offsetCenter: [0, 0],
          },
          progress: {
            show: true,
            overlap: true,
            roundCap: true
          },
          axisLine: {
            roundCap: true
          },
          axisTick: {
            lineStyle: {
              color: 'white' //눈금 색
            }
          },
          splitLine: {
            lineStyle: {
              color: 'white' //분할선 색
            }
          },
          axisLabel: {
            color: 'white' //눈금 숫자 색
          },
          data: [
            {
              value: data1,
              name: 'LAST MONTH',
              title: {
                color: 'white',
                offsetCenter: ['-80%', '90%']
              },
              detail: {
                offsetCenter: ['-80%', '110%'],
                //backgroundColor:'red'
                width: 80
              }
            },
            {
              value: data2,
              name: 'CURRENT MONTH',
              title: {
                color: 'white',
                offsetCenter: ['80%', '90%']
              },
              detail: {
                offsetCenter: ['80%', '110%'],
                width: 80
              }
            }
          ],
          title: {
            fontSize: 14
          },
          detail: {
            width: 40,
            height: 14,
            fontSize: 14,
            color: '#fff',
            backgroundColor: 'inherit',
            borderRadius: 3,
            formatter: `{value}${unit}`
          }
        }
      ]
    };
  }

  function DateOnChange(event) {
    if (event.target.id === 'infra') {
      date.infra = event.target.value;
    } else if (event.target.id === 'server') {
      date.server = event.target.value;
    } else if (event.target.id === 'airConditioner') {
      date.airConditioner = event.target.value;
    }

    setDate(date);
    DrawLineChart();
  }

  function OnChartReady(instance) {
    chartRefs.current.push(instance);
  }

  return (
    <div onContextMenu={(event)=>{ event.preventDefault(); }}>
      <div style={{ backgroundColor: backgroundMainColor, paddingTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
        <img src={logo} width={'200px'} height={'40px'} style={{ marginLeft: 10 }}/>

        <div style={{ marginRight: gridGap }}>
          <button title={refreshTime} onClick={Reload} style={buttonStyle}>⟳</button>
          {!isMobile && <button title={document.fullscreenElement ? '최소화' : '최대화'} onClick={ChangeFullscreen} style={buttonStyle}>◱</button>}
        </div>
      </div>
      <div style={gridStyle}>

        <div style={gridItemStyle}>
          <ReactECharts option={totalGaugeOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>
        <div style={gridItemStyle}>
          <ReactECharts option={infraGaugeOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>

        <div style={gridItemStyle}>
          <ReactECharts option={serverGaugeOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>

        <div style={gridItemStyle}>
          <ReactECharts option={airConditionerGaugeOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>
        <div style={gridItemStyle}>
          <ReactECharts option={co2GaugeOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>

        <div style={gridItemStyle}>
          <input type='date' id='infra' value={date.infra} onChange={DateOnChange} /*style={{backgroundColor: 'rgb(34, 55, 82)', color: 'white' }}*//>
          <ReactECharts option={infraLineOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>

        <div style={gridItemStyle}>
          <input type='date' id='server' value={date.server} onChange={DateOnChange}/>
          <ReactECharts option={serverLineOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>

        <div style={gridItemStyle}>
          <input type='date' id='airConditioner' value={date.airConditioner} onChange={DateOnChange}/>
          <ReactECharts option={airConditionerLineOption} style={eChartsStyle} onChartReady={OnChartReady}/>
        </div>
      </div>
    </div>
  );
}

export default YeonooMonitoring2;